import React, { useState, useEffect, createContext, useContext, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar } from "./components/ui/calendar";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Badge } from "./components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Textarea } from "./components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Sheet, SheetContent, SheetTrigger } from "./components/ui/sheet";
import { ScrollArea } from "./components/ui/scroll-area";
import { Toaster, toast } from "sonner";
import { getTranslation } from "./translations";
import { 
  Menu, X, ShoppingCart, User, Calendar as CalendarIcon, Clock, 
  MapPin, Phone, Mail, ChevronRight, Check, Star, Scissors, 
  Sparkles, Droplet, Palette, Globe, Plus, Minus, Trash2, Eye, Edit, LogOut,
  Home as HomeIcon, Image as ImageIcon, Package, Users, Award, Settings
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Context
const AppContext = createContext();

const useApp = () => useContext(AppContext);

// Assets
const ASSETS = {
  logo: "https://customer-assets.emergentagent.com/job_92bba06a-7e2c-421f-98ee-53af52d4a86e/artifacts/ov3xio0k_1.png",
  heroBg: "https://customer-assets.emergentagent.com/job_barber-booking-app-22/artifacts/v7cuhu6g_IMG_6946.jpg",
  aboutBg: "https://customer-assets.emergentagent.com/job_barber-booking-app-22/artifacts/c3f2mnc0_IMG_6938.jpg",
  interior: "https://customer-assets.emergentagent.com/job_barber-booking-app-22/artifacts/352xeaui_IMG_6940.jpg",
  barber: "https://customer-assets.emergentagent.com/job_92bba06a-7e2c-421f-98ee-53af52d4a86e/artifacts/q7yhc370_IMG_6958.jpg",
  team: "https://customer-assets.emergentagent.com/job_barber-booking-app-22/artifacts/0qd7msoe_IMG_6943.jpg",
  steam: "https://customer-assets.emergentagent.com/job_92bba06a-7e2c-421f-98ee-53af52d4a86e/artifacts/4xjhvbj5_03d74d1a-2f0b-47aa-b087-44451640728d.JPG",
  haircut: "https://customer-assets.emergentagent.com/job_barber-booking-app-22/artifacts/zbspyfai_IMG_6945.jpg",
};

// ============= LAYOUT =============
const Layout = ({ children }) => {
  const { lang, setLang, cart, user, logout, t } = useApp();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [footerClicks, setFooterClicks] = useState(0);
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const navigate = useNavigate();

  const handleFooterLogoClick = () => {
    const newClicks = footerClicks + 1;
    setFooterClicks(newClicks);
    if (newClicks >= 5) {
      setShowAdminLogin(true);
      setFooterClicks(0);
    }
    setTimeout(() => setFooterClicks(0), 2000);
  };

  const navItems = [
    { path: "/", label: t("nav_home"), icon: HomeIcon },
    { path: "/book", label: t("nav_book"), icon: CalendarIcon },
    { path: "/shop", label: t("nav_shop"), icon: Package },
    { path: "/gallery", label: t("nav_gallery"), icon: ImageIcon },
  ];

  return (
    <div className="min-h-screen bg-void" dir={lang === "ar" ? "rtl" : "ltr"}>
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-void-highlight">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3">
              <img src={ASSETS.logo} alt="Arabian Barber" className="h-12 w-12 object-contain" />
              <span className="text-gradient-gold font-bold text-xl hidden sm:block">Arabian Barber</span>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-6">
              {navItems.map(item => (
                <Link key={item.path} to={item.path} className="text-gray-300 hover:text-gold transition-colors text-sm font-medium">
                  {item.label}
                </Link>
              ))}
            </div>

            {/* Right Side */}
            <div className="flex items-center gap-3">
              {/* Language Toggle */}
              <button
                onClick={() => setLang(lang === "en" ? "ar" : "en")}
                className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-void-surface border border-void-highlight hover:border-gold transition-colors text-sm"
                data-testid="lang-toggle"
              >
                <Globe className="w-4 h-4" />
                <span>{lang === "en" ? "AR" : "EN"}</span>
              </button>

              {/* Cart */}
              <Link to="/cart" className="relative p-2 hover:bg-void-surface rounded-md transition-colors" data-testid="cart-btn">
                <ShoppingCart className="w-5 h-5" />
                {cart.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-gold text-void text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                    {cart.reduce((a, b) => a + b.quantity, 0)}
                  </span>
                )}
              </Link>

              {/* User */}
              {user ? (
                <div className="flex items-center gap-2">
                  <Link to="/profile" className="p-2 hover:bg-void-surface rounded-md transition-colors" data-testid="profile-btn">
                    <User className="w-5 h-5" />
                  </Link>
                  <button onClick={logout} className="p-2 hover:bg-void-surface rounded-md text-red-400" data-testid="logout-btn">
                    <LogOut className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <Link to="/login" className="btn-gold px-4 py-2 rounded-md text-sm font-bold" data-testid="login-btn">
                  {t("nav_login")}
                </Link>
              )}

              {/* Mobile Menu */}
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild className="md:hidden">
                  <button className="p-2"><Menu className="w-6 h-6" /></button>
                </SheetTrigger>
                <SheetContent side={lang === "ar" ? "right" : "left"} className="bg-void-surface border-void-highlight">
                  <div className="flex flex-col gap-4 mt-8">
                    {navItems.map(item => (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-void-highlight text-gray-200"
                      >
                        <item.icon className="w-5 h-5 text-gold" />
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-16">{children}</main>

      {/* Footer */}
      <footer className="bg-void-surface border-t border-void-highlight py-12 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <img
                src={ASSETS.logo}
                alt="Arabian Barber"
                className="h-16 w-16 cursor-pointer"
                onClick={handleFooterLogoClick}
                data-testid="footer-logo"
              />
              <p className="text-gold font-bold mt-2">{lang === "ar" ? "منذ 2024" : "Since 2024"}</p>
              <p className="text-gray-400 mt-2 text-sm">{t("hero_description")}</p>
            </div>
            <div>
              <h4 className="text-gold font-bold mb-4">{t("contact_title")}</h4>
              <div className="space-y-2 text-sm text-gray-400">
                <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-gold" /> {t("footer_address")}</p>
                <p className="flex items-center gap-2"><Phone className="w-4 h-4 text-gold" /> 07914117879</p>
                <p className="flex items-center gap-2"><Mail className="w-4 h-4 text-gold" /> info@arabianbarber.co.uk</p>
              </div>
            </div>
            <div>
              <h4 className="text-gold font-bold mb-4">{t("contact_hours")}</h4>
              <div className="space-y-2 text-sm text-gray-400">
                <p>{t("contact_mon_sat")}</p>
                <p>{t("contact_sunday")}</p>
              </div>
            </div>
            <div>
              <h4 className="text-gold font-bold mb-4">Quick Links</h4>
              <div className="space-y-2">
                {navItems.map(item => (
                  <Link key={item.path} to={item.path} className="block text-sm text-gray-400 hover:text-gold transition-colors">
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
          <div className="border-t border-void-highlight mt-8 pt-8 text-center text-gray-500 text-sm">
            {t("footer_copyright")}
          </div>
        </div>
      </footer>

      {/* Admin Login Modal */}
      <Dialog open={showAdminLogin} onOpenChange={setShowAdminLogin}>
        <DialogContent className="bg-void-surface border-void-highlight">
          <DialogHeader>
            <DialogTitle className="text-gold">{t("admin_title")}</DialogTitle>
          </DialogHeader>
          <AdminLoginForm onSuccess={() => { setShowAdminLogin(false); navigate("/admin"); }} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============= ADMIN LOGIN FORM =============
const AdminLoginForm = ({ onSuccess }) => {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { setAdminToken, t } = useApp();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem("adminToken", res.data.token);
      setAdminToken(res.data.token);
      toast.success("Admin login successful!");
      onSuccess();
    } catch {
      toast.error("Invalid password");
    }
    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Input
        type="password"
        placeholder={t("admin_password")}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        className="input-dark"
        data-testid="admin-password-input"
      />
      <Button type="submit" className="w-full btn-gold" disabled={loading} data-testid="admin-login-btn">
        {loading ? t("common_loading") : t("admin_login")}
      </Button>
    </form>
  );
};

// ============= HOME PAGE =============
const HomePage = () => {
  const { t, lang } = useApp();
  const [services, setServices] = useState([]);
  const [reviews, setReviews] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/services`).then(res => setServices(res.data)).catch(console.error);
    axios.get(`${API}/reviews`).then(res => setReviews(res.data)).catch(console.error);
  }, []);

  return (
    <div>
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={ASSETS.heroBg} alt="Arabian Barber Shop" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/70" />
        </div>
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <motion.img
            src={ASSETS.logo}
            alt="Arabian Barber"
            className="w-32 h-32 sm:w-40 sm:h-40 mx-auto mb-6"
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ duration: 0.8, type: "spring" }}
          />
          <motion.h1
            className="text-4xl sm:text-5xl lg:text-7xl font-bold text-gradient-gold mb-2"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            {t("hero_title")}
          </motion.h1>
          <motion.p
            className="text-xl sm:text-2xl text-gold-hover mb-4"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            {t("hero_subtitle")}
          </motion.p>
          <motion.p
            className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            {t("hero_description")}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
          >
            <Button
              onClick={() => navigate("/book")}
              className="btn-gold text-lg px-8 py-4 rounded-md animate-pulse-gold"
              data-testid="hero-book-btn"
            >
              {t("hero_cta")}
              <ChevronRight className="ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-4" data-testid="services-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gradient-gold mb-4">{t("services_title")}</h2>
            <p className="text-gray-400">{t("services_subtitle")}</p>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
            {services.map((service, i) => (
              <motion.div
                key={service.id}
                className="card-dark rounded-lg p-4 sm:p-6 text-center group cursor-pointer"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                onClick={() => navigate("/book")}
                data-testid={`service-card-${i}`}
              >
                <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 rounded-full bg-void-highlight flex items-center justify-center group-hover:bg-gold/10 transition-colors">
                  <Scissors className="w-6 h-6 sm:w-8 sm:h-8 text-gold" />
                </div>
                <h3 className="font-bold text-sm sm:text-base mb-2">{lang === "ar" ? service.name_ar : service.name_en}</h3>
                <p className="text-gold font-bold">£{service.price.toFixed(2)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Google Reviews Section */}
      <section className="py-20 px-4 bg-void-surface" data-testid="reviews-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center justify-center gap-3 mb-4">
              <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              <h2 className="text-3xl sm:text-4xl font-bold text-gradient-gold">
                {lang === "ar" ? "آراء العملاء" : "Customer Reviews"}
              </h2>
            </div>
            <div className="flex items-center justify-center gap-1 mb-2">
              {[1,2,3,4,5].map(i => (
                <Star key={i} className="w-6 h-6 fill-yellow-400 text-yellow-400" />
              ))}
              <span className="ml-2 text-xl font-bold">5.0</span>
            </div>
            <p className="text-gray-400">{lang === "ar" ? "بناءً على تقييمات Google" : "Based on Google Reviews"}</p>
          </motion.div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.slice(0, 6).map((review, i) => (
              <motion.div
                key={review.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Card className="card-dark h-full" data-testid={`review-${i}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center text-gold font-bold text-lg">
                        {review.author_name.charAt(0)}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-bold">{review.author_name}</h4>
                        <div className="flex items-center gap-1 mt-1">
                          {[...Array(review.rating)].map((_, idx) => (
                            <Star key={idx} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                          ))}
                        </div>
                      </div>
                      <span className="text-xs text-gray-500">{review.time}</span>
                    </div>
                    <p className="text-gray-300 text-sm leading-relaxed">{review.text}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
          
          <div className="text-center mt-8">
            <a 
              href="https://www.google.com/search?q=arabian+barber+nottingham+reviews" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-gold hover:text-gold-hover transition-colors"
            >
              {lang === "ar" ? "اقرأ المزيد من التقييمات على Google" : "Read more reviews on Google"}
              <ChevronRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 px-4" data-testid="about-section">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <img src={ASSETS.aboutBg} alt="Shop Interior" className="rounded-lg shadow-2xl" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gradient-gold mb-2">
              {lang === "ar" ? "مرحباً بكم في الحلاق العربي" : "Welcome to Arabian Barber"}
            </h2>
            <p className="text-gold-hover text-lg mb-4">
              {lang === "ar" ? "منذ 2024 • خبرة أكثر من 10 سنوات" : "Since 2024 • Over 10 Years Experience"}
            </p>
            <p className="text-gray-300 mb-6 leading-relaxed">
              {lang === "ar" 
                ? "نحن نجمع بين فن الحلاقة التقليدية والتقنيات الحديثة لنقدم لك تجربة فريدة من نوعها في قلب نوتنغهام. فريقنا من المحترفين يقدم لك أفضل الخدمات بأعلى معايير الجودة."
                : "We combine the art of traditional barbering with modern techniques to give you a unique grooming experience in the heart of Nottingham. Our team of professionals with over 10 years of experience delivers the highest quality services."
              }
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <Star className="w-6 h-6 text-gold" />
                <span>{lang === "ar" ? "خبرة 10+ سنوات" : "10+ Years Experience"}</span>
              </div>
              <div className="flex items-center gap-3">
                <Sparkles className="w-6 h-6 text-gold" />
                <span>{lang === "ar" ? "منتجات فاخرة" : "Premium Products"}</span>
              </div>
              <div className="flex items-center gap-3">
                <Clock className="w-6 h-6 text-gold" />
                <span>{lang === "ar" ? "حجز مرن" : "Flexible Booking"}</span>
              </div>
              <div className="flex items-center gap-3">
                <Award className="w-6 h-6 text-gold" />
                <span>{lang === "ar" ? "نقاط الولاء" : "Loyalty Rewards"}</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 px-4 bg-void-surface" data-testid="map-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gradient-gold mb-4">{t("contact_title")}</h2>
            <p className="text-gray-400 flex items-center justify-center gap-2">
              <MapPin className="text-gold" /> 61 Maid Marian Way, Nottingham NG1 6AJ
            </p>
          </motion.div>
          <div className="rounded-lg overflow-hidden border border-void-highlight">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2403.8!2d-1.1545!3d52.9533!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4879c17d4d3a3f3f%3A0x1234567890!2s61%20Maid%20Marian%20Way%2C%20Nottingham%20NG1%206AJ!5e0!3m2!1sen!2suk!4v1704067200000"
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              title="Arabian Barber Location"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

// ============= BOOKING PAGE =============
const BookingPage = () => {
  const { t, lang, user } = useApp();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedTime, setSelectedTime] = useState("");
  const [formData, setFormData] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    notes: ""
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/services`).then(res => setServices(res.data)).catch(console.error);
  }, []);

  useEffect(() => {
    if (selectedDate) {
      const dateStr = selectedDate.toISOString().split("T")[0];
      axios.get(`${API}/bookings/slots?date=${dateStr}`)
        .then(res => setAvailableSlots(res.data.slots))
        .catch(console.error);
    }
  }, [selectedDate]);

  const toggleService = (serviceId) => {
    setSelectedServices(prev =>
      prev.includes(serviceId) ? prev.filter(id => id !== serviceId) : [...prev, serviceId]
    );
  };

  const totalPrice = services
    .filter(s => selectedServices.includes(s.id))
    .reduce((sum, s) => sum + s.price, 0);

  const handleSubmit = async () => {
    if (!selectedDate || !selectedTime || selectedServices.length === 0) {
      toast.error("Please complete all steps");
      return;
    }
    setLoading(true);
    try {
      const booking = {
        customer_id: user?.id || null,
        customer_name: formData.name,
        customer_email: formData.email,
        customer_phone: formData.phone,
        services: selectedServices,
        date: selectedDate.toISOString().split("T")[0],
        time: selectedTime,
        notes: formData.notes
      };
      await axios.post(`${API}/bookings`, booking);
      toast.success(t("booking_success"));
      navigate("/profile");
    } catch (err) {
      toast.error(t("common_error"));
    }
    setLoading(false);
  };

  const steps = [
    { num: 1, label: t("booking_select_services") },
    { num: 2, label: t("booking_select_date") },
    { num: 3, label: t("booking_select_time") },
    { num: 4, label: t("booking_your_details") }
  ];

  return (
    <div className="min-h-screen py-20 px-4" data-testid="booking-page">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl sm:text-4xl font-bold text-gradient-gold text-center mb-8">{t("booking_title")}</h1>

        {/* Progress Bar */}
        <div className="flex items-center justify-center mb-12">
          {steps.map((s, i) => (
            <React.Fragment key={s.num}>
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-colors ${
                  step >= s.num ? "bg-gold text-void" : "bg-void-surface text-gray-500 border border-void-highlight"
                }`}
              >
                {step > s.num ? <Check className="w-5 h-5" /> : s.num}
              </div>
              {i < steps.length - 1 && (
                <div className={`w-12 sm:w-20 h-1 mx-2 ${step > s.num ? "bg-gold" : "bg-void-highlight"}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {/* Step 1: Services */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
            >
              <h2 className="text-xl font-bold mb-6 text-center">{t("booking_select_services")}</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {services.map(service => (
                  <div
                    key={service.id}
                    onClick={() => toggleService(service.id)}
                    className={`p-4 rounded-lg cursor-pointer transition-all ${
                      selectedServices.includes(service.id)
                        ? "bg-gold/20 border-2 border-gold"
                        : "bg-void-surface border border-void-highlight hover:border-gold/50"
                    }`}
                    data-testid={`service-select-${service.id}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <Scissors className="w-5 h-5 text-gold" />
                      {selectedServices.includes(service.id) && <Check className="w-5 h-5 text-gold" />}
                    </div>
                    <h3 className="font-bold text-sm">{lang === "ar" ? service.name_ar : service.name_en}</h3>
                    <p className="text-gold text-sm">£{service.price.toFixed(2)}</p>
                  </div>
                ))}
              </div>
              {selectedServices.length > 0 && (
                <div className="mt-6 p-4 bg-void-surface rounded-lg flex justify-between items-center">
                  <span>{lang === "ar" ? "المجموع:" : "Total:"}</span>
                  <span className="text-gold font-bold text-xl">£{totalPrice.toFixed(2)}</span>
                </div>
              )}
              <div className="mt-8 flex justify-end">
                <Button
                  onClick={() => setStep(2)}
                  disabled={selectedServices.length === 0}
                  className="btn-gold"
                  data-testid="next-step-1"
                >
                  {t("common_next")} <ChevronRight className="ml-2" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 2: Date */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              className="flex flex-col items-center"
            >
              <h2 className="text-xl font-bold mb-6">{t("booking_select_date")}</h2>
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                disabled={(date) => date < new Date() || date > new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)}
                className="rounded-lg border border-void-highlight bg-void-surface p-4"
                data-testid="booking-calendar"
              />
              <div className="mt-8 flex gap-4">
                <Button onClick={() => setStep(1)} variant="outline" className="border-gold text-gold hover:bg-gold hover:text-void">
                  {t("common_back")}
                </Button>
                <Button onClick={() => setStep(3)} disabled={!selectedDate} className="btn-gold" data-testid="next-step-2">
                  {t("common_next")} <ChevronRight className="ml-2" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 3: Time */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
            >
              <h2 className="text-xl font-bold mb-6 text-center">{t("booking_select_time")}</h2>
              {availableSlots.length > 0 ? (
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-3">
                  {availableSlots.map(slot => (
                    <button
                      key={slot}
                      onClick={() => setSelectedTime(slot)}
                      className={`p-3 rounded-lg text-sm font-medium transition-all ${
                        selectedTime === slot
                          ? "bg-gold text-void"
                          : "bg-void-surface border border-void-highlight hover:border-gold"
                      }`}
                      data-testid={`time-slot-${slot}`}
                    >
                      {slot}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-400">{t("common_loading")}</p>
              )}
              <div className="mt-8 flex justify-center gap-4">
                <Button onClick={() => setStep(2)} variant="outline" className="border-gold text-gold hover:bg-gold hover:text-void">
                  {t("common_back")}
                </Button>
                <Button onClick={() => setStep(4)} disabled={!selectedTime} className="btn-gold" data-testid="next-step-3">
                  {t("common_next")} <ChevronRight className="ml-2" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 4: Details */}
          {step === 4 && (
            <motion.div
              key="step4"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
            >
              <h2 className="text-xl font-bold mb-6 text-center">{t("booking_your_details")}</h2>
              <div className="max-w-md mx-auto space-y-4">
                <Input
                  placeholder={t("booking_name")}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input-dark"
                  data-testid="booking-name"
                />
                <Input
                  type="email"
                  placeholder={t("booking_email")}
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="input-dark"
                  data-testid="booking-email"
                />
                <Input
                  placeholder={t("booking_phone")}
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="input-dark"
                  data-testid="booking-phone"
                />
                <Textarea
                  placeholder={t("booking_notes")}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="input-dark min-h-[100px]"
                  data-testid="booking-notes"
                />

                {/* Summary */}
                <Card className="bg-void-surface border-void-highlight">
                  <CardContent className="p-4">
                    <h3 className="font-bold mb-4 text-gold">{lang === "ar" ? "ملخص الحجز" : "Booking Summary"}</h3>
                    <div className="space-y-2 text-sm">
                      <p><CalendarIcon className="inline w-4 h-4 mr-2 text-gold" />{selectedDate?.toLocaleDateString()}</p>
                      <p><Clock className="inline w-4 h-4 mr-2 text-gold" />{selectedTime}</p>
                      <div>
                        <Scissors className="inline w-4 h-4 mr-2 text-gold" />
                        {services.filter(s => selectedServices.includes(s.id)).map(s => lang === "ar" ? s.name_ar : s.name_en).join(", ")}
                      </div>
                      <div className="pt-2 border-t border-void-highlight flex justify-between">
                        <span className="font-bold">{t("cart_total")}</span>
                        <span className="text-gold font-bold">£{totalPrice.toFixed(2)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
              <div className="mt-8 flex justify-center gap-4">
                <Button onClick={() => setStep(3)} variant="outline" className="border-gold text-gold hover:bg-gold hover:text-void">
                  {t("common_back")}
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={loading || !formData.name || !formData.email || !formData.phone}
                  className="btn-gold"
                  data-testid="confirm-booking"
                >
                  {loading ? t("common_loading") : t("booking_confirm")}
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

// ============= SHOP PAGE =============
const ShopPage = () => {
  const { t, lang, addToCart } = useApp();
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const url = category ? `${API}/products?category=${category}` : `${API}/products`;
    axios.get(url)
      .then(res => setProducts(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [category]);

  const categories = [
    { value: "", label: t("shop_all") },
    { value: "hair_cream", label: t("shop_hair_cream") },
    { value: "cologne", label: t("shop_cologne") },
    { value: "beard_oil", label: t("shop_beard_oil") },
  ];

  return (
    <div className="min-h-screen py-20 px-4" data-testid="shop-page">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl font-bold text-gradient-gold mb-4">{t("shop_title")}</h1>
          <p className="text-gray-400">{t("shop_subtitle")}</p>
        </div>

        {/* Categories */}
        <div className="flex flex-wrap justify-center gap-3 mb-8">
          {categories.map(cat => (
            <button
              key={cat.value}
              onClick={() => setCategory(cat.value)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                category === cat.value
                  ? "bg-gold text-void"
                  : "bg-void-surface border border-void-highlight hover:border-gold"
              }`}
              data-testid={`category-${cat.value || "all"}`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="text-center text-gray-400">{t("common_loading")}</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {products.map((product, i) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
              >
                <Card className="card-dark overflow-hidden group" data-testid={`product-${product.id}`}>
                  <div className="aspect-square overflow-hidden">
                    <img
                      src={product.image_url}
                      alt={lang === "ar" ? product.name_ar : product.name_en}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-bold mb-1">{lang === "ar" ? product.name_ar : product.name_en}</h3>
                    <p className="text-sm text-gray-400 mb-3 line-clamp-2">
                      {lang === "ar" ? product.description_ar : product.description_en}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-gold font-bold text-lg">£{product.price.toFixed(2)}</span>
                      <Button
                        size="sm"
                        onClick={() => addToCart(product)}
                        className="btn-gold text-sm"
                        data-testid={`add-cart-${product.id}`}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ============= CART PAGE =============
const CartPage = () => {
  const { t, lang, cart, updateCartQuantity, removeFromCart, user } = useApp();
  const navigate = useNavigate();
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [formData, setFormData] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || ""
  });
  const [loading, setLoading] = useState(false);

  const total = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);

  const handleCheckout = async () => {
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error("Please fill all details");
      return;
    }
    setLoading(true);
    try {
      const order = {
        customer_id: user?.id || null,
        customer_name: formData.name,
        customer_email: formData.email,
        customer_phone: formData.phone,
        items: cart.map(item => ({ product_id: item.id, quantity: item.quantity })),
        payment_method: paymentMethod
      };
      const res = await axios.post(`${API}/orders`, order);
      
      if (paymentMethod === "card") {
        // Redirect to Stripe
        const paymentRes = await axios.post(`${API}/payments/create-checkout`, {
          order_id: res.data.id,
          amount: total,
          origin_url: window.location.origin
        });
        window.location.href = paymentRes.data.url;
      } else {
        toast.success(t("checkout_success"));
        localStorage.removeItem("cart");
        window.location.href = "/profile";
      }
    } catch (err) {
      toast.error(t("common_error"));
    }
    setLoading(false);
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen py-20 px-4 flex flex-col items-center justify-center">
        <ShoppingCart className="w-16 h-16 text-gray-600 mb-4" />
        <h2 className="text-xl font-bold mb-4">{t("cart_empty")}</h2>
        <Button onClick={() => navigate("/shop")} className="btn-gold">{t("nav_shop")}</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-20 px-4" data-testid="cart-page">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-gradient-gold mb-8">{t("cart_title")}</h1>
        
        <div className="grid md:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="md:col-span-2 space-y-4">
            {cart.map(item => (
              <Card key={item.id} className="card-dark" data-testid={`cart-item-${item.id}`}>
                <CardContent className="p-4 flex gap-4">
                  <img src={item.image_url} alt={item.name_en} className="w-20 h-20 object-cover rounded-lg" />
                  <div className="flex-1">
                    <h3 className="font-bold">{lang === "ar" ? item.name_ar : item.name_en}</h3>
                    <p className="text-gold">£{item.price.toFixed(2)}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        onClick={() => updateCartQuantity(item.id, item.quantity - 1)}
                        className="p-1 rounded bg-void-highlight hover:bg-gold hover:text-void"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="w-8 text-center">{item.quantity}</span>
                      <button
                        onClick={() => updateCartQuantity(item.id, item.quantity + 1)}
                        className="p-1 rounded bg-void-highlight hover:bg-gold hover:text-void"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="ml-auto p-1 text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Checkout Form */}
          <div>
            <Card className="card-dark sticky top-24">
              <CardHeader>
                <CardTitle className="text-gold">{t("checkout_title")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Input
                  placeholder={t("booking_name")}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input-dark"
                  data-testid="checkout-name"
                />
                <Input
                  type="email"
                  placeholder={t("booking_email")}
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="input-dark"
                  data-testid="checkout-email"
                />
                <Input
                  placeholder={t("booking_phone")}
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="input-dark"
                  data-testid="checkout-phone"
                />

                <div className="space-y-2">
                  <label className="text-sm text-gray-400">{t("checkout_payment_method")}</label>
                  {["card", "cash", "transfer"].map(method => (
                    <button
                      key={method}
                      onClick={() => setPaymentMethod(method)}
                      className={`w-full p-3 rounded-lg text-left transition-all flex items-center gap-3 ${
                        paymentMethod === method
                          ? "bg-gold/20 border-2 border-gold"
                          : "bg-void-surface border border-void-highlight"
                      }`}
                      data-testid={`payment-${method}`}
                    >
                      {paymentMethod === method && <Check className="w-4 h-4 text-gold" />}
                      {t(`checkout_${method}`)}
                    </button>
                  ))}
                </div>

                <div className="pt-4 border-t border-void-highlight">
                  <div className="flex justify-between text-lg font-bold">
                    <span>{t("cart_total")}</span>
                    <span className="text-gold">£{total.toFixed(2)}</span>
                  </div>
                </div>

                <Button
                  onClick={handleCheckout}
                  disabled={loading}
                  className="w-full btn-gold"
                  data-testid="checkout-btn"
                >
                  {loading ? t("common_loading") : t("checkout_place_order")}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============= GALLERY PAGE =============
const GalleryPage = () => {
  const { t, lang } = useApp();
  const [images, setImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    axios.get(`${API}/gallery`).then(res => setImages(res.data)).catch(console.error);
  }, []);

  return (
    <div className="min-h-screen py-20 px-4" data-testid="gallery-page">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl font-bold text-gradient-gold mb-4">{t("gallery_title")}</h1>
          <p className="text-gray-400">{t("gallery_subtitle")}</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((img, i) => (
            <motion.div
              key={img.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: i * 0.1 }}
              className="aspect-square overflow-hidden rounded-lg cursor-pointer group"
              onClick={() => setSelectedImage(img)}
              data-testid={`gallery-img-${i}`}
            >
              <img
                src={img.image_url}
                alt={lang === "ar" ? img.title_ar : img.title_en}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
            </motion.div>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="bg-void-surface border-void-highlight max-w-4xl">
          {selectedImage && (
            <img
              src={selectedImage.image_url}
              alt={lang === "ar" ? selectedImage.title_ar : selectedImage.title_en}
              className="w-full h-auto rounded-lg"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============= PROFILE PAGE =============
const ProfilePage = () => {
  const { t, lang, user, token } = useApp();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      axios.get(`${API}/bookings`, { headers }),
      axios.get(`${API}/orders`, { headers })
    ]).then(([bookingsRes, ordersRes]) => {
      setBookings(bookingsRes.data);
      setOrders(ordersRes.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, [user, token, navigate]);

  if (!user) return null;

  const statusColors = {
    pending: "bg-yellow-500/20 text-yellow-400",
    confirmed: "bg-blue-500/20 text-blue-400",
    completed: "bg-green-500/20 text-green-400",
    cancelled: "bg-red-500/20 text-red-400",
    paid: "bg-green-500/20 text-green-400"
  };

  return (
    <div className="min-h-screen py-20 px-4" data-testid="profile-page">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gold/20 flex items-center justify-center">
            <User className="w-10 h-10 text-gold" />
          </div>
          <h1 className="text-2xl font-bold">{user.name}</h1>
          <p className="text-gray-400">{user.email}</p>
        </div>

        {/* Loyalty Points */}
        <Card className="card-dark mb-8" data-testid="loyalty-card">
          <CardContent className="p-6 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Award className="w-10 h-10 text-gold" />
              <div>
                <p className="text-sm text-gray-400">{t("profile_loyalty")}</p>
                <p className="text-3xl font-bold text-gold">{user.loyalty_points}</p>
              </div>
            </div>
            <Badge className="bg-gold/20 text-gold">{t("profile_points")}</Badge>
          </CardContent>
        </Card>

        <Tabs defaultValue="bookings" className="w-full">
          <TabsList className="w-full bg-void-surface mb-6">
            <TabsTrigger value="bookings" className="flex-1 data-[state=active]:bg-gold data-[state=active]:text-void">
              {t("profile_bookings")}
            </TabsTrigger>
            <TabsTrigger value="orders" className="flex-1 data-[state=active]:bg-gold data-[state=active]:text-void">
              {t("profile_orders")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="bookings">
            {loading ? (
              <p className="text-center text-gray-400">{t("common_loading")}</p>
            ) : bookings.length === 0 ? (
              <p className="text-center text-gray-400">{t("profile_no_bookings")}</p>
            ) : (
              <div className="space-y-4">
                {bookings.map(booking => (
                  <Card key={booking.id} className="card-dark" data-testid={`booking-${booking.id}`}>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <p className="font-bold">{booking.date} - {booking.time}</p>
                          <p className="text-sm text-gray-400">
                            {booking.services.map(s => lang === "ar" ? s.name_ar : s.name_en).join(", ")}
                          </p>
                        </div>
                        <Badge className={statusColors[booking.status]}>{t(`status_${booking.status}`)}</Badge>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gold font-bold">£{booking.total_price.toFixed(2)}</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="orders">
            {loading ? (
              <p className="text-center text-gray-400">{t("common_loading")}</p>
            ) : orders.length === 0 ? (
              <p className="text-center text-gray-400">{t("profile_no_orders")}</p>
            ) : (
              <div className="space-y-4">
                {orders.map(order => (
                  <Card key={order.id} className="card-dark" data-testid={`order-${order.id}`}>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <p className="font-bold">{new Date(order.created_at).toLocaleDateString()}</p>
                          <p className="text-sm text-gray-400">
                            {order.items.map(item => `${lang === "ar" ? item.product_name_ar : item.product_name_en} x${item.quantity}`).join(", ")}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Badge className={statusColors[order.payment_status]}>{t(`status_${order.payment_status}`)}</Badge>
                          <Badge className={statusColors[order.order_status]}>{t(`status_${order.order_status}`)}</Badge>
                        </div>
                      </div>
                      <span className="text-gold font-bold">£{order.total_price.toFixed(2)}</span>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// ============= AUTH PAGE =============
const AuthPage = () => {
  const { t, login } = useApp();
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", password: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const endpoint = isLogin ? "/auth/login" : "/auth/register";
      const payload = isLogin ? { email: formData.email, password: formData.password } : formData;
      const res = await axios.post(`${API}${endpoint}`, payload);
      login(res.data.token, res.data.customer);
      toast.success(t("common_success"));
      navigate("/profile");
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common_error"));
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen py-20 px-4 flex items-center justify-center" data-testid="auth-page">
      <Card className="card-dark w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-gold text-center">{isLogin ? t("auth_login") : t("auth_register")}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <>
                <Input
                  placeholder={t("auth_name")}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input-dark"
                  data-testid="auth-name"
                />
                <Input
                  placeholder={t("auth_phone")}
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="input-dark"
                  data-testid="auth-phone"
                />
              </>
            )}
            <Input
              type="email"
              placeholder={t("auth_email")}
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="input-dark"
              data-testid="auth-email"
            />
            <Input
              type="password"
              placeholder={t("auth_password")}
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="input-dark"
              data-testid="auth-password"
            />
            <Button type="submit" className="w-full btn-gold" disabled={loading} data-testid="auth-submit">
              {loading ? t("common_loading") : isLogin ? t("auth_login") : t("auth_register")}
            </Button>
          </form>
          <p className="text-center mt-4 text-sm text-gray-400">
            {isLogin ? t("auth_dont_have") : t("auth_already_have")}{" "}
            <button onClick={() => setIsLogin(!isLogin)} className="text-gold hover:underline">
              {isLogin ? t("auth_register") : t("auth_login")}
            </button>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

// ============= PAYMENT SUCCESS PAGE =============
const PaymentSuccessPage = () => {
  const { t } = useApp();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("checking");
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId) return;
    
    const checkStatus = async () => {
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setStatus("success");
          localStorage.removeItem("cart");
        } else {
          setStatus("pending");
        }
      } catch {
        setStatus("error");
      }
    };

    // Poll for status
    const interval = setInterval(checkStatus, 2000);
    checkStatus();

    return () => clearInterval(interval);
  }, [sessionId]);

  return (
    <div className="min-h-screen py-20 px-4 flex items-center justify-center" data-testid="payment-success">
      <Card className="card-dark w-full max-w-md text-center">
        <CardContent className="p-8">
          {status === "checking" && (
            <>
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gold/20 flex items-center justify-center animate-pulse">
                <Clock className="w-8 h-8 text-gold" />
              </div>
              <h2 className="text-xl font-bold mb-2">{t("common_loading")}</h2>
            </>
          )}
          {status === "success" && (
            <>
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
                <Check className="w-8 h-8 text-green-400" />
              </div>
              <h2 className="text-xl font-bold text-green-400 mb-2">{t("checkout_success")}</h2>
              <Link to="/profile" className="btn-gold inline-block px-6 py-2 rounded-md mt-4">
                {t("nav_profile")}
              </Link>
            </>
          )}
          {status === "error" && (
            <>
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
                <X className="w-8 h-8 text-red-400" />
              </div>
              <h2 className="text-xl font-bold text-red-400 mb-2">{t("common_error")}</h2>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ============= ADMIN PANEL =============
const AdminPanel = () => {
  const { t, lang, adminToken } = useApp();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [showProductModal, setShowProductModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [showPointsModal, setShowPointsModal] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  
  // Form states
  const [productForm, setProductForm] = useState({ name_en: "", name_ar: "", description_en: "", description_ar: "", price: "", category: "", image_url: "", stock: "100" });
  const [categoryForm, setCategoryForm] = useState({ name_en: "", name_ar: "", slug: "" });
  const [galleryForm, setGalleryForm] = useState({ image_url: "", title_en: "", title_ar: "" });
  const [serviceForm, setServiceForm] = useState({ name_en: "", name_ar: "", price: "", duration_minutes: "30" });
  const [pointsForm, setPointsForm] = useState({ points: "", reason: "" });
  const [bookingForm, setBookingForm] = useState({ customer_name: "", customer_email: "", customer_phone: "", services: [], date: "", time: "", notes: "" });
  const [availableSlots, setAvailableSlots] = useState([]);

  const headers = { Authorization: `Bearer ${adminToken}` };

  useEffect(() => {
    if (!adminToken) {
      navigate("/");
      return;
    }
    fetchData();
  }, [adminToken, navigate]);

  const fetchData = async () => {
    try {
      const [bookingsRes, productsRes, ordersRes, galleryRes, customersRes, servicesRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/bookings`, { headers }),
        axios.get(`${API}/products`),
        axios.get(`${API}/orders`, { headers }),
        axios.get(`${API}/gallery`),
        axios.get(`${API}/loyalty/customers`, { headers }),
        axios.get(`${API}/services`),
        axios.get(`${API}/categories`)
      ]);
      setBookings(bookingsRes.data);
      setProducts(productsRes.data);
      setOrders(ordersRes.data);
      setGallery(galleryRes.data);
      setCustomers(customersRes.data);
      setServices(servicesRes.data);
      setCategories(categoriesRes.data);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const updateBookingStatus = async (id, status) => {
    try {
      await axios.put(`${API}/bookings/${id}`, { status }, { headers });
      toast.success("Booking updated");
      fetchData();
    } catch {
      toast.error("Error updating booking");
    }
  };

  const updateOrderStatus = async (id, orderStatus, paymentStatus) => {
    try {
      await axios.put(`${API}/orders/${id}?order_status=${orderStatus}&payment_status=${paymentStatus}`, {}, { headers });
      toast.success("Order updated");
      fetchData();
    } catch {
      toast.error("Error updating order");
    }
  };

  // Booking CRUD
  const fetchAvailableSlots = async (date) => {
    try {
      const res = await axios.get(`${API}/bookings/slots?date=${date}`);
      setAvailableSlots(res.data.slots);
    } catch {
      setAvailableSlots([]);
    }
  };

  const openBookingModal = (booking = null) => {
    if (booking) {
      setBookingForm({
        customer_name: booking.customer_name,
        customer_email: booking.customer_email,
        customer_phone: booking.customer_phone,
        services: booking.services.map(s => s.id),
        date: booking.date,
        time: booking.time,
        notes: booking.notes || "",
        status: booking.status
      });
      setEditingItem(booking);
      fetchAvailableSlots(booking.date);
    } else {
      const today = new Date().toISOString().split('T')[0];
      setBookingForm({ customer_name: "", customer_email: "", customer_phone: "", services: [], date: today, time: "", notes: "", status: "pending" });
      setEditingItem(null);
      fetchAvailableSlots(today);
    }
    setShowBookingModal(true);
  };

  const saveBooking = async () => {
    try {
      if (editingItem) {
        // Update existing booking
        await axios.put(`${API}/bookings/${editingItem.id}`, bookingForm, { headers });
        toast.success("Booking updated - Email notification sent!");
      } else {
        // Create new booking from admin
        await axios.post(`${API}/bookings/admin`, bookingForm, { headers });
        toast.success("Booking created - Confirmation email sent!");
      }
      setShowBookingModal(false);
      fetchData();
    } catch (err) {
      toast.error("Error saving booking");
    }
  };

  const toggleBookingService = (serviceId) => {
    setBookingForm(prev => ({
      ...prev,
      services: prev.services.includes(serviceId)
        ? prev.services.filter(id => id !== serviceId)
        : [...prev.services, serviceId]
    }));
  };

  // Product CRUD
  const openProductModal = (product = null) => {
    if (product) {
      setProductForm({ ...product, price: product.price.toString(), stock: product.stock.toString() });
      setEditingItem(product);
    } else {
      setProductForm({ name_en: "", name_ar: "", description_en: "", description_ar: "", price: "", category: categories[0]?.slug || "", image_url: "", stock: "100" });
      setEditingItem(null);
    }
    setShowProductModal(true);
  };

  const saveProduct = async () => {
    try {
      const data = { ...productForm, price: parseFloat(productForm.price), stock: parseInt(productForm.stock) };
      if (editingItem) {
        await axios.put(`${API}/products/${editingItem.id}`, data, { headers });
      } else {
        await axios.post(`${API}/products`, data, { headers });
      }
      toast.success("Product saved");
      setShowProductModal(false);
      fetchData();
    } catch {
      toast.error("Error saving product");
    }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await axios.delete(`${API}/products/${id}`, { headers });
      toast.success("Product deleted");
      fetchData();
    } catch {
      toast.error("Error deleting product");
    }
  };

  // Category CRUD
  const openCategoryModal = (category = null) => {
    if (category) {
      setCategoryForm({ ...category });
      setEditingItem(category);
    } else {
      setCategoryForm({ name_en: "", name_ar: "", slug: "" });
      setEditingItem(null);
    }
    setShowCategoryModal(true);
  };

  const saveCategory = async () => {
    try {
      if (editingItem) {
        await axios.put(`${API}/categories/${editingItem.id}`, categoryForm, { headers });
      } else {
        await axios.post(`${API}/categories`, categoryForm, { headers });
      }
      toast.success("Category saved");
      setShowCategoryModal(false);
      fetchData();
    } catch {
      toast.error("Error saving category");
    }
  };

  const deleteCategory = async (id) => {
    if (!window.confirm("Delete this category?")) return;
    try {
      await axios.delete(`${API}/categories/${id}`, { headers });
      toast.success("Category deleted");
      fetchData();
    } catch {
      toast.error("Error deleting category");
    }
  };

  // Gallery CRUD
  const openGalleryModal = () => {
    setGalleryForm({ image_url: "", title_en: "", title_ar: "" });
    setShowGalleryModal(true);
  };

  const saveGalleryImage = async () => {
    try {
      await axios.post(`${API}/gallery`, galleryForm, { headers });
      toast.success("Image added");
      setShowGalleryModal(false);
      fetchData();
    } catch {
      toast.error("Error adding image");
    }
  };

  const deleteGalleryImage = async (id) => {
    if (!window.confirm("Delete this image?")) return;
    try {
      await axios.delete(`${API}/gallery/${id}`, { headers });
      toast.success("Image deleted");
      fetchData();
    } catch {
      toast.error("Error deleting image");
    }
  };

  // Service Price Update
  const openServiceModal = (service) => {
    setServiceForm({ ...service, price: service.price.toString(), duration_minutes: service.duration_minutes.toString() });
    setEditingItem(service);
    setShowServiceModal(true);
  };

  const saveService = async () => {
    try {
      await axios.put(`${API}/services/${editingItem.id}/price`, {
        price: parseFloat(serviceForm.price),
        duration_minutes: parseInt(serviceForm.duration_minutes),
        name_en: serviceForm.name_en,
        name_ar: serviceForm.name_ar
      }, { headers });
      toast.success("Service updated");
      setShowServiceModal(false);
      fetchData();
    } catch {
      toast.error("Error updating service");
    }
  };

  // Loyalty Points with custom amount
  const openPointsModal = (customer) => {
    setSelectedCustomer(customer);
    setPointsForm({ points: "", reason: "" });
    setShowPointsModal(true);
  };

  const adjustPoints = async () => {
    if (!pointsForm.points || !pointsForm.reason) {
      toast.error("Please enter points and reason");
      return;
    }
    try {
      await axios.post(`${API}/loyalty/adjust`, {
        customer_id: selectedCustomer.id,
        points: parseInt(pointsForm.points),
        reason: pointsForm.reason
      }, { headers });
      toast.success("Points adjusted");
      setShowPointsModal(false);
      fetchData();
    } catch {
      toast.error("Error adjusting points");
    }
  };

  const tabs = [
    { id: "bookings", label: t("admin_bookings"), icon: CalendarIcon },
    { id: "products", label: t("admin_products"), icon: Package },
    { id: "categories", label: "Categories", icon: Settings },
    { id: "orders", label: t("admin_orders"), icon: ShoppingCart },
    { id: "gallery", label: t("admin_gallery"), icon: ImageIcon },
    { id: "loyalty", label: t("admin_loyalty"), icon: Award },
    { id: "services", label: t("admin_services"), icon: Settings }
  ];

  const statusColors = {
    pending: "bg-yellow-500/20 text-yellow-400",
    confirmed: "bg-blue-500/20 text-blue-400",
    completed: "bg-green-500/20 text-green-400",
    cancelled: "bg-red-500/20 text-red-400",
    paid: "bg-green-500/20 text-green-400",
    processing: "bg-blue-500/20 text-blue-400",
    delivered: "bg-green-500/20 text-green-400"
  };

  if (!adminToken) return null;

  return (
    <div className="min-h-screen py-20 px-4" data-testid="admin-panel">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-gradient-gold mb-8">{t("admin_title")}</h1>

        <div className="flex flex-wrap gap-2 mb-8">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                activeTab === tab.id ? "bg-gold text-void" : "bg-void-surface hover:bg-void-highlight"
              }`}
              data-testid={`admin-tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-center text-gray-400">{t("common_loading")}</p>
        ) : (
          <>
            {/* Bookings Tab */}
            {activeTab === "bookings" && (
              <div>
                <div className="flex justify-end mb-4">
                  <Button onClick={() => openBookingModal()} className="btn-gold" data-testid="add-booking-btn">
                    <Plus className="w-4 h-4 mr-2" /> Add Booking
                  </Button>
                </div>
                <div className="space-y-4">
                  {bookings.length === 0 ? (
                    <p className="text-center text-gray-400">No bookings yet</p>
                  ) : bookings.map(booking => (
                    <Card key={booking.id} className="card-dark" data-testid={`admin-booking-${booking.id}`}>
                      <CardContent className="p-4">
                        <div className="flex flex-wrap justify-between items-start gap-4">
                          <div>
                            <p className="font-bold">{booking.customer_name}</p>
                            <p className="text-sm text-gray-400">{booking.customer_phone} | {booking.customer_email}</p>
                            <p className="text-gold mt-2">{booking.date} - {booking.time}</p>
                            <p className="text-sm">{booking.services.map(s => lang === "ar" ? s.name_ar : s.name_en).join(", ")}</p>
                          </div>
                          <div className="flex flex-col gap-2 items-end">
                            <Badge className={statusColors[booking.status]}>{booking.status}</Badge>
                            <span className="text-gold font-bold">£{booking.total_price.toFixed(2)}</span>
                            <div className="flex gap-2">
                              <Button size="sm" onClick={() => openBookingModal(booking)} variant="outline" className="border-gold text-gold">
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Select onValueChange={(val) => updateBookingStatus(booking.id, val)} defaultValue={booking.status}>
                                <SelectTrigger className="w-28 bg-void-surface border-void-highlight">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="pending">Pending</SelectItem>
                                  <SelectItem value="confirmed">Confirmed</SelectItem>
                                  <SelectItem value="completed">Completed</SelectItem>
                                  <SelectItem value="cancelled">Cancelled</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Products Tab */}
            {activeTab === "products" && (
              <div>
                <div className="flex justify-end mb-4">
                  <Button onClick={() => openProductModal()} className="btn-gold" data-testid="add-product-btn">
                    <Plus className="w-4 h-4 mr-2" /> Add Product
                  </Button>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {products.map(product => (
                    <Card key={product.id} className="card-dark" data-testid={`admin-product-${product.id}`}>
                      <CardContent className="p-4">
                        <img src={product.image_url} alt={product.name_en} className="w-full h-40 object-cover rounded-lg mb-4" />
                        <h3 className="font-bold">{lang === "ar" ? product.name_ar : product.name_en}</h3>
                        <p className="text-gold font-bold">£{product.price.toFixed(2)}</p>
                        <p className="text-sm text-gray-400">Stock: {product.stock} | {product.category}</p>
                        <div className="flex gap-2 mt-4">
                          <Button size="sm" onClick={() => openProductModal(product)} variant="outline" className="border-gold text-gold">
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button size="sm" onClick={() => deleteProduct(product.id)} variant="destructive">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Categories Tab */}
            {activeTab === "categories" && (
              <div>
                <div className="flex justify-end mb-4">
                  <Button onClick={() => openCategoryModal()} className="btn-gold" data-testid="add-category-btn">
                    <Plus className="w-4 h-4 mr-2" /> Add Category
                  </Button>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {categories.map(cat => (
                    <Card key={cat.id} className="card-dark" data-testid={`admin-category-${cat.id}`}>
                      <CardContent className="p-4">
                        <h3 className="font-bold text-lg">{lang === "ar" ? cat.name_ar : cat.name_en}</h3>
                        <p className="text-sm text-gray-400">Slug: {cat.slug}</p>
                        <div className="flex gap-2 mt-4">
                          <Button size="sm" onClick={() => openCategoryModal(cat)} variant="outline" className="border-gold text-gold">
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button size="sm" onClick={() => deleteCategory(cat.id)} variant="destructive">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Orders Tab */}
            {activeTab === "orders" && (
              <div className="space-y-4">
                {orders.length === 0 ? (
                  <p className="text-center text-gray-400">No orders yet</p>
                ) : orders.map(order => (
                  <Card key={order.id} className="card-dark" data-testid={`admin-order-${order.id}`}>
                    <CardContent className="p-4">
                      <div className="flex flex-wrap justify-between items-start gap-4">
                        <div>
                          <p className="font-bold">{order.customer_name}</p>
                          <p className="text-sm text-gray-400">{order.customer_phone}</p>
                          <p className="text-sm mt-2">{order.items.map(i => `${i.product_name_en} x${i.quantity}`).join(", ")}</p>
                          <p className="text-gold font-bold mt-2">£{order.total_price.toFixed(2)}</p>
                        </div>
                        <div className="flex flex-col gap-2 items-end">
                          <Badge className={statusColors[order.payment_status]}>Payment: {order.payment_status}</Badge>
                          <Badge className={statusColors[order.order_status]}>Order: {order.order_status}</Badge>
                          <Select onValueChange={(val) => updateOrderStatus(order.id, val, order.payment_status)} defaultValue={order.order_status}>
                            <SelectTrigger className="w-32 bg-void-surface border-void-highlight">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="processing">Processing</SelectItem>
                              <SelectItem value="delivered">Delivered</SelectItem>
                              <SelectItem value="cancelled">Cancelled</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Gallery Tab */}
            {activeTab === "gallery" && (
              <div>
                <div className="flex justify-end mb-4">
                  <Button onClick={openGalleryModal} className="btn-gold" data-testid="add-gallery-btn">
                    <Plus className="w-4 h-4 mr-2" /> Add Image
                  </Button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {gallery.map(img => (
                    <div key={img.id} className="relative group" data-testid={`admin-gallery-${img.id}`}>
                      <img src={img.image_url} alt={img.title_en} className="w-full aspect-square object-cover rounded-lg" />
                      <button
                        onClick={() => deleteGalleryImage(img.id)}
                        className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Loyalty Tab */}
            {activeTab === "loyalty" && (
              <div className="space-y-4">
                {customers.length === 0 ? (
                  <p className="text-center text-gray-400">No customers yet</p>
                ) : customers.map(customer => (
                  <Card key={customer.id} className="card-dark" data-testid={`admin-customer-${customer.id}`}>
                    <CardContent className="p-4 flex flex-wrap justify-between items-center gap-4">
                      <div>
                        <p className="font-bold">{customer.name}</p>
                        <p className="text-sm text-gray-400">{customer.email}</p>
                        <p className="text-sm text-gray-400">{customer.phone}</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-center">
                          <p className="text-2xl font-bold text-gold">{customer.loyalty_points}</p>
                          <p className="text-xs text-gray-400">points</p>
                        </div>
                        <Button onClick={() => openPointsModal(customer)} className="btn-gold" data-testid={`adjust-points-${customer.id}`}>
                          Adjust Points
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Services Tab */}
            {activeTab === "services" && (
              <div className="grid md:grid-cols-2 gap-4">
                {services.map(service => (
                  <Card key={service.id} className="card-dark" data-testid={`admin-service-${service.id}`}>
                    <CardContent className="p-4 flex justify-between items-center">
                      <div>
                        <p className="font-bold">{lang === "ar" ? service.name_ar : service.name_en}</p>
                        <p className="text-sm text-gray-400">{service.duration_minutes} mins</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-gold font-bold text-xl">£{service.price.toFixed(2)}</span>
                        <Button size="sm" onClick={() => openServiceModal(service)} variant="outline" className="border-gold text-gold">
                          <Edit className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Product Modal */}
      <Dialog open={showProductModal} onOpenChange={setShowProductModal}>
        <DialogContent className="bg-void-surface border-void-highlight max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gold">{editingItem ? "Edit Product" : "Add Product"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input placeholder="Name (English)" value={productForm.name_en} onChange={(e) => setProductForm({...productForm, name_en: e.target.value})} className="input-dark" />
            <Input placeholder="Name (Arabic)" value={productForm.name_ar} onChange={(e) => setProductForm({...productForm, name_ar: e.target.value})} className="input-dark" />
            <Textarea placeholder="Description (English)" value={productForm.description_en} onChange={(e) => setProductForm({...productForm, description_en: e.target.value})} className="input-dark" />
            <Textarea placeholder="Description (Arabic)" value={productForm.description_ar} onChange={(e) => setProductForm({...productForm, description_ar: e.target.value})} className="input-dark" />
            <Input type="number" placeholder="Price (£)" value={productForm.price} onChange={(e) => setProductForm({...productForm, price: e.target.value})} className="input-dark" />
            <Input type="number" placeholder="Stock" value={productForm.stock} onChange={(e) => setProductForm({...productForm, stock: e.target.value})} className="input-dark" />
            <Select value={productForm.category} onValueChange={(val) => setProductForm({...productForm, category: val})}>
              <SelectTrigger className="bg-void-surface border-void-highlight">
                <SelectValue placeholder="Select Category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.slug}>{cat.name_en}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input placeholder="Image URL (paste any image link)" value={productForm.image_url} onChange={(e) => setProductForm({...productForm, image_url: e.target.value})} className="input-dark" />
            {productForm.image_url && <img src={productForm.image_url} alt="Preview" className="w-full h-32 object-cover rounded-lg" />}
            <Button onClick={saveProduct} className="w-full btn-gold">Save Product</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Category Modal */}
      <Dialog open={showCategoryModal} onOpenChange={setShowCategoryModal}>
        <DialogContent className="bg-void-surface border-void-highlight max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gold">{editingItem ? "Edit Category" : "Add Category"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input placeholder="Name (English)" value={categoryForm.name_en} onChange={(e) => setCategoryForm({...categoryForm, name_en: e.target.value})} className="input-dark" />
            <Input placeholder="Name (Arabic)" value={categoryForm.name_ar} onChange={(e) => setCategoryForm({...categoryForm, name_ar: e.target.value})} className="input-dark" />
            <Input placeholder="Slug (e.g. beard_oil)" value={categoryForm.slug} onChange={(e) => setCategoryForm({...categoryForm, slug: e.target.value})} className="input-dark" />
            <Button onClick={saveCategory} className="w-full btn-gold">Save Category</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Gallery Modal */}
      <Dialog open={showGalleryModal} onOpenChange={setShowGalleryModal}>
        <DialogContent className="bg-void-surface border-void-highlight max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gold">Add Gallery Image</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input placeholder="Image URL (paste any image link)" value={galleryForm.image_url} onChange={(e) => setGalleryForm({...galleryForm, image_url: e.target.value})} className="input-dark" />
            <Input placeholder="Title (English)" value={galleryForm.title_en} onChange={(e) => setGalleryForm({...galleryForm, title_en: e.target.value})} className="input-dark" />
            <Input placeholder="Title (Arabic)" value={galleryForm.title_ar} onChange={(e) => setGalleryForm({...galleryForm, title_ar: e.target.value})} className="input-dark" />
            {galleryForm.image_url && <img src={galleryForm.image_url} alt="Preview" className="w-full h-40 object-cover rounded-lg" />}
            <Button onClick={saveGalleryImage} className="w-full btn-gold">Add Image</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Service Modal */}
      <Dialog open={showServiceModal} onOpenChange={setShowServiceModal}>
        <DialogContent className="bg-void-surface border-void-highlight max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gold">Edit Service</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input placeholder="Name (English)" value={serviceForm.name_en} onChange={(e) => setServiceForm({...serviceForm, name_en: e.target.value})} className="input-dark" />
            <Input placeholder="Name (Arabic)" value={serviceForm.name_ar} onChange={(e) => setServiceForm({...serviceForm, name_ar: e.target.value})} className="input-dark" />
            <Input type="number" placeholder="Price (£)" value={serviceForm.price} onChange={(e) => setServiceForm({...serviceForm, price: e.target.value})} className="input-dark" />
            <Input type="number" placeholder="Duration (minutes)" value={serviceForm.duration_minutes} onChange={(e) => setServiceForm({...serviceForm, duration_minutes: e.target.value})} className="input-dark" />
            <Button onClick={saveService} className="w-full btn-gold">Update Service</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Points Modal */}
      <Dialog open={showPointsModal} onOpenChange={setShowPointsModal}>
        <DialogContent className="bg-void-surface border-void-highlight max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gold">Adjust Points for {selectedCustomer?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-400">Current points: <span className="text-gold font-bold">{selectedCustomer?.loyalty_points}</span></p>
            <Input 
              type="number" 
              placeholder="Points (use negative to deduct, e.g. -50)" 
              value={pointsForm.points} 
              onChange={(e) => setPointsForm({...pointsForm, points: e.target.value})} 
              className="input-dark" 
            />
            <Input 
              placeholder="Reason (e.g. Discount offer, Bonus points)" 
              value={pointsForm.reason} 
              onChange={(e) => setPointsForm({...pointsForm, reason: e.target.value})} 
              className="input-dark" 
            />
            <div className="flex gap-2">
              <Button onClick={() => setPointsForm({...pointsForm, points: "25"})} variant="outline" className="flex-1 border-green-500 text-green-500">+25</Button>
              <Button onClick={() => setPointsForm({...pointsForm, points: "50"})} variant="outline" className="flex-1 border-green-500 text-green-500">+50</Button>
              <Button onClick={() => setPointsForm({...pointsForm, points: "-25"})} variant="outline" className="flex-1 border-red-500 text-red-500">-25</Button>
              <Button onClick={() => setPointsForm({...pointsForm, points: "-50"})} variant="outline" className="flex-1 border-red-500 text-red-500">-50</Button>
            </div>
            <Button onClick={adjustPoints} className="w-full btn-gold">Apply Points</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Booking Modal */}
      <Dialog open={showBookingModal} onOpenChange={setShowBookingModal}>
        <DialogContent className="bg-void-surface border-void-highlight max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gold">{editingItem ? "Edit Booking" : "Create Booking"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Customer Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input 
                placeholder="Customer Name" 
                value={bookingForm.customer_name} 
                onChange={(e) => setBookingForm({...bookingForm, customer_name: e.target.value})} 
                className="input-dark" 
              />
              <Input 
                placeholder="Phone Number" 
                value={bookingForm.customer_phone} 
                onChange={(e) => setBookingForm({...bookingForm, customer_phone: e.target.value})} 
                className="input-dark" 
              />
            </div>
            <Input 
              type="email"
              placeholder="Email Address" 
              value={bookingForm.customer_email} 
              onChange={(e) => setBookingForm({...bookingForm, customer_email: e.target.value})} 
              className="input-dark" 
            />
            
            {/* Date and Time */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Date</label>
                <Input 
                  type="date"
                  value={bookingForm.date} 
                  onChange={(e) => {
                    setBookingForm({...bookingForm, date: e.target.value, time: ""});
                    fetchAvailableSlots(e.target.value);
                  }} 
                  className="input-dark" 
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Time</label>
                <Select value={bookingForm.time} onValueChange={(val) => setBookingForm({...bookingForm, time: val})}>
                  <SelectTrigger className="bg-void-surface border-void-highlight">
                    <SelectValue placeholder="Select time" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableSlots.map(slot => (
                      <SelectItem key={slot} value={slot}>{slot}</SelectItem>
                    ))}
                    {editingItem && !availableSlots.includes(editingItem.time) && (
                      <SelectItem value={editingItem.time}>{editingItem.time} (current)</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Services Selection */}
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Services</label>
              <div className="grid grid-cols-2 gap-2">
                {services.map(service => (
                  <button
                    key={service.id}
                    type="button"
                    onClick={() => toggleBookingService(service.id)}
                    className={`p-3 rounded-lg text-left text-sm transition-all ${
                      bookingForm.services.includes(service.id)
                        ? "bg-gold/20 border-2 border-gold"
                        : "bg-void-highlight border border-void-highlight hover:border-gold/50"
                    }`}
                  >
                    <div className="flex justify-between">
                      <span>{lang === "ar" ? service.name_ar : service.name_en}</span>
                      {bookingForm.services.includes(service.id) && <Check className="w-4 h-4 text-gold" />}
                    </div>
                    <span className="text-gold text-xs">£{service.price.toFixed(2)}</span>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Notes */}
            <Textarea 
              placeholder="Notes (optional)" 
              value={bookingForm.notes} 
              onChange={(e) => setBookingForm({...bookingForm, notes: e.target.value})} 
              className="input-dark" 
            />
            
            {/* Total */}
            <div className="p-4 bg-void-highlight rounded-lg flex justify-between items-center">
              <span className="text-gray-400">Total:</span>
              <span className="text-gold font-bold text-xl">
                £{services.filter(s => bookingForm.services.includes(s.id)).reduce((sum, s) => sum + s.price, 0).toFixed(2)}
              </span>
            </div>
            
            <Button 
              onClick={saveBooking} 
              className="w-full btn-gold"
              disabled={!bookingForm.customer_name || !bookingForm.customer_phone || !bookingForm.date || !bookingForm.time || bookingForm.services.length === 0}
            >
              {editingItem ? "Update Booking" : "Create Booking"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============= APP PROVIDER =============
const AppProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem("lang") || "en");
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem("token"));
  const [adminToken, setAdminToken] = useState(() => localStorage.getItem("adminToken"));
  const [cart, setCart] = useState(() => {
    const saved = localStorage.getItem("cart");
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    localStorage.setItem("lang", lang);
  }, [lang]);

  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    if (token) {
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setUser(res.data))
        .catch(() => { setToken(null); localStorage.removeItem("token"); });
    }
  }, [token]);

  const t = useCallback((key) => getTranslation(lang, key), [lang]);

  const login = (newToken, userData) => {
    setToken(newToken);
    setUser(userData);
    localStorage.setItem("token", newToken);
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem("token");
  };

  const addToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        return prev.map(item => item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item);
      }
      return [...prev, { ...product, quantity: 1 }];
    });
    toast.success(`${lang === "ar" ? product.name_ar : product.name_en} added to cart`);
  };

  const updateCartQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      setCart(prev => prev.filter(item => item.id !== productId));
    } else {
      setCart(prev => prev.map(item => item.id === productId ? { ...item, quantity } : item));
    }
  };

  const removeFromCart = (productId) => {
    setCart(prev => prev.filter(item => item.id !== productId));
  };

  return (
    <AppContext.Provider value={{
      lang, setLang, t,
      user, token, login, logout,
      adminToken, setAdminToken,
      cart, addToCart, updateCartQuantity, removeFromCart
    }}>
      {children}
    </AppContext.Provider>
  );
};

// ============= MAIN APP =============
function App() {
  return (
    <BrowserRouter>
      <AppProvider>
        <Layout>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/book" element={<BookingPage />} />
            <Route path="/shop" element={<ShopPage />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/gallery" element={<GalleryPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/login" element={<AuthPage />} />
            <Route path="/payment-success" element={<PaymentSuccessPage />} />
            <Route path="/admin" element={<AdminPanel />} />
          </Routes>
        </Layout>
        <Toaster position="top-center" richColors />
      </AppProvider>
    </BrowserRouter>
  );
}

export default App;
